import React, { useState, useEffect, useContext, useRef } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Menu, X, ChevronDown } from 'lucide-react';
import { UserContext } from '../context/UserContext';
import '../styles/Header.css';

const Header = () => {
  const { user, setUser } = useContext(UserContext);
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation(); // Add useLocation to detect route changes
  const dropdownRef = useRef(null); // Ref for dropdown menu
  const mobileMenuRef = useRef(null); // Ref for mobile menu

  // Handle scroll effect
  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Close menus on route change
  useEffect(() => {
    setDropdownOpen(false);
    setMobileMenuOpen(false);
  }, [location.pathname]); // Trigger when the route changes

  // Handle click outside to close dropdown and mobile menu
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setDropdownOpen(false);
      }
      if (mobileMenuRef.current && !mobileMenuRef.current.contains(event.target)) {
        setMobileMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleLogout = async () => {
    try {
      await fetch(`${process.env.REACT_APP_API_URL}/api/auth/logout`, {
        method: 'POST',
        credentials: 'include',
      });
      setUser(null);
      navigate('/');
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  const navItems = [
    { title: 'Home', link: '/' },
    { title: 'About Us', link: '/about-us' },
    { title: 'Blogs', link: '/blogs' },
    { title: 'Contact', link: '/contact' },
    { title: 'Franchise Enquiry', link: '/franchise' },
  ];

  return (
    <header className={`site-header ${scrolled ? 'scrolled' : ''} ${mobileMenuOpen ? 'menu-open' : ''}`}>
      <div className="header-container">
        <div className="logo-container">
          <Link to="/" className="logo">
            <div className="logo-image">
              <img src="/assets/images/beaver_logo1.png" alt="Beaver Tours Logo" className="company-logo" />
            </div>
            <div className="logo-text">
              <span className="primary">Beaver Tours</span>
              <span className="secondary">Explore the Journey, Live the Freedom!</span>
            </div>
          </Link>
        </div>

        <nav className={`desktop-nav ${mobileMenuOpen ? 'hidden' : ''}`}>
          <ul className="nav-list">
            {navItems.map((item, index) => (
              <li key={index} className="nav-item">
                <Link to={item.link} className="nav-link">
                  {item.title}
                </Link>
              </li>
            ))}
            {user ? (
              <>
              <li className="nav-item">
                  <Link to="/book" className="nav-button book-button">
                    Rent a Vehicle
                  </Link>
                </li>
                <li className="nav-item dropdown" ref={dropdownRef}>
                  <div
                    className="nav-link dropdown-toggle"
                    onClick={() => setDropdownOpen(!dropdownOpen)}
                  >
                    Hi, {user.name} <ChevronDown size={16} />
                  </div>
                  {dropdownOpen && (
                    <ul className="dropdown-menu">
                      <li>
                        <Link to="/profile" className="dropdown-item">
                          Profile
                        </Link>
                      </li>
                      {user.role !== 'admin' && (
                        <li>
                          <Link to="/orders" className="dropdown-item">
                            My Orders
                          </Link>
                        </li>
                      )}
                      {user.role === 'admin' && (
                        <li>
                          <Link to="/admin" className="dropdown-item">
                            Admin
                          </Link>
                        </li>
                      )}
                      <li>
                        <button onClick={handleLogout} className="dropdown-item logout-button">
                          Logout
                        </button>
                      </li>
                    </ul>
                  )}
                </li>
              </>
            ) : (
              <>
              
                <li className="nav-item">
                  <Link to="/book" className="nav-button book-button">
                    Rent a Vehicle
                  </Link>
                </li>
                <li className="nav-item">
                  <Link to="/login" className="nav-button login-button">
                    Login / Signup
                  </Link>
                </li>
              </>
            )}
          </ul>
        </nav>

        <button className="mobile-menu-toggle" onClick={() => setMobileMenuOpen(!mobileMenuOpen)}>
          {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
        </button>

        <nav className={`mobile-nav ${mobileMenuOpen ? 'active' : ''}`} ref={mobileMenuRef}>
          <ul className="mobile-nav-list">
            {navItems.map((item, index) => (
              <li key={index} className="mobile-nav-item">
                <Link
                  to={item.link}
                  className="mobile-nav-link"
                  onClick={() => setMobileMenuOpen(false)} // Close mobile menu on link click
                >
                  {item.title}
                </Link>
              </li>
            ))}
            {user ? (
              <>
                <li className="mobile-nav-item">
                  <span className="mobile-nav-text">Hi, {user.name}</span>
                </li>
                <li className="mobile-nav-item">
                  <Link
                    to="/profile"
                    className="mobile-nav-button"
                    onClick={() => setMobileMenuOpen(false)} // Close mobile menu on link click
                  >
                    Profile
                  </Link>
                </li>
                {user.role !== 'admin' && (
                  <li className="mobile-nav-item">
                    <Link
                      to="/orders"
                      className="mobile-nav-button orders-button"
                      onClick={() => setMobileMenuOpen(false)} // Close mobile menu on link click
                    >
                      My Orders
                    </Link>
                  </li>
                )}
                {user.role === 'admin' && (
                  <li className="mobile-nav-item">
                    <Link
                      to="/admin"
                      className="mobile-nav-button"
                      onClick={() => setMobileMenuOpen(false)} // Close mobile menu on link click
                    >
                      Admin
                    </Link>
                  </li>
                )}
                <li className="mobile-nav-item">
                  <button
                    onClick={() => {
                      handleLogout();
                      setMobileMenuOpen(false); // Close mobile menu on logout
                    }}
                    className="mobile-nav-button logout-button"
                  >
                    Logout
                  </button>
                </li>
              </>
            ) : (
              <>
                <li className="mobile-nav-item">
                  <Link
                    to="/login"
                    className="mobile-nav-button login-button"
                    onClick={() => setMobileMenuOpen(false)} // Close mobile menu on link click
                  >
                    Login / Signup
                  </Link>
                </li>
                <li className="mobile-nav-item">
                  <Link
                    to="/book"
                    className="mobile-nav-button book-button"
                    onClick={() => setMobileMenuOpen(false)} // Close mobile menu on link click
                  >
                    Book Now
                  </Link>
                </li>
              </>
            )}
          </ul>
        </nav>
      </div>
    </header>
  );
};

export default Header;